import { Instance, getSnapshot, types, flow } from "mobx-state-tree";
import {
  asyncOpStateType,
  INITIAL_ASYNC_OP_STATE,
} from "../../types/mst/async-op-state";
import { createDataPointActions } from "./createDataPointActions";
import { saveSchemasActions } from "./saveSchemasActions";
import { saveFileNameActions } from "./saveFileNameActions";
import { saveMiniSiteConfigActions } from "./saveMiniSiteConfigActions";
import _ from "lodash";
import {
  ProjectProps,
  DataFileProps,
  VidPartProps,
  ModProps,
} from "../platformSchema";
import {
  ModInput,
  PlayerSettingsInput,
  UpdateProjectInput,
  ExperimentInput,
  ExperimentOptimizationTechnique,
  VidPartInput,
  OverlayInput,
  FontInput,
  OverlayAudioInput,
  FlowNodeType,
} from "../../API";
import { emptySchema } from "../../helpers/jsonSchema.helpers";
import { AsyncOpState } from "../../types/enums/async-op-states";
import { flattenObject } from "../../helpers/object.helpers";
import { generateCSV } from "../../helpers/file.helpers";
import { downloadFile } from "../../helpers/file.helpers";
import { getAssetLayerPairs } from "./getAssetLayerPairs";
import {
  Additional,
  EditVideoExposedToPlatformType,
  EditVideoInteractiveType,
  EditVideoTextType,
  EditVideoTypes,
  ExposedToPlatformModInput,
  InteractiveModInput,
  TextLayerInput,
} from "../../types/EditVideoTypes";
import { updateAsyncStatus } from "../async-op-states";
import {
  changeProjectExperimentOptimizationTechnique,
  getLatestProjectVersion,
  publishProject,
  saveFontOverridesDraft,
  saveFlowDiagramDraft,
  saveFlowDiagram,
  saveOverlay,
  saveOverlayVersion as saveOverlayDraft,
  savePublished,
  updateVersion,
} from "../../utils/projectTools";
import { rootStore } from "../Root";
import { DynamicThumbnailsModel } from "./DynamicThumbnailModel";
import { ExperimentStore } from "../ExperimentStore";
import { IFlowDiagram, FLOW_DIAGRAM_NODE_TYPE } from "@blings/blings-player";
import { getJsonDataFromUrl } from "./jsonDataMemo";
import type { ProjectVersion } from "../../API";
import { pubsub } from "../../utils/pubsub";
import { subscriptionManager, Subscription } from "../../utils/subscriptionManager";

// Standalone function to process video part JSON data and return assetLayerPairs
export function processVideoPartJsonData(jsonData: Animation) {
  if (!jsonData) return { assetLayerPairs: null };

  const [assetLayerPairs, mainPairs] = getAssetLayerPairs(jsonData);
  assetLayerPairs[mainAssetKey] = mainPairs;

  Object.keys(assetLayerPairs).forEach((key) => {
    if (key.indexOf(sequenceLayerId) === 0) {
      delete assetLayerPairs[key];
    }
  });

  // Return the processed assetLayerPairs
  return {
    assetLayerPairs,
  };
}

export const mainAssetKey = "main_asset_keys";
export const sequenceLayerId = "sequence_";
export const seqImgId = "imgSeq_";
export const PLATFORM_TEXT_CONTROL = "PLATFORM_TEXT_CONTROL";
export const PLATFORM_INTERACTIVE_CONTROL = "PLATFORM_INTERACTIVE_CONTROL";
export const PLATFORM_EXPOSED_CONTROL = "PLATFORM_EXPOSED_CONTROL";
export const DataFileModel = types.model("DataFile", {
  ...DataFileProps,
  // fileName: types.string,
  // tag: types.maybeNull(types.string),
  // createdAt: types.maybeNull(types.string),
});
// Define FlowDiagramNodeType as a proper enum

const FlowDiagramEdgeModel = types.model("FlowDiagramEdge", {
  source: types.string,
  target: types.string,
  sourceHandle: types.optional(
    types.maybeNull(types.union(types.boolean, types.number, types.string)),
    null
  ),
  index: types.optional(types.maybeNull(types.number), null),
});
const FlowDiagramNodeModel = types.model("FlowDiagramNode", {
  id: types.string,
  label: types.string,
  type: types.enumeration("FlowDiagramNodeType", Object.values(FlowNodeType)),
  data: types.maybeNull(types.string),
  lineupId: types.maybeNull(types.string),
});

const serializeSourceHandle = (handle: unknown): string | null => {
  if (handle === undefined || handle === null) return null;
  try {
    return JSON.stringify(handle);
  } catch {
    return String(handle);
  }
};

export const deserializeSourceHandle = (
  handle: unknown
): string | number | boolean | null => {
  if (handle === undefined || handle === null) return null;
  if (typeof handle !== "string") return handle as string | number | boolean;
  try {
    return JSON.parse(handle);
  } catch {
    return handle;
  }
};
export const FlowDiagramModel = types
  .model("FlowDiagram", {
    edges: types.array(FlowDiagramEdgeModel),
    nodes: types.array(FlowDiagramNodeModel),
  })
  .views((self) => ({
    get lineups() {
      return self.nodes.reduce<Array<string>>((acc, node) => {
        if (node.lineupId) {
          acc.push(node.lineupId);
        }
        return acc;
      }, []);
    },
    /**
     * Transform this MST model to the FlowDiagram type expected by NodeEditor.
     * 
     * The MST model defines a single FlowNode type that can have different type values,
     * while the NodeEditor expects a union of specific node types (ISceneNode | IConditionNode | ILineupNode).
     * This view handles the transformation between these two type systems.
     */
    get asNodeEditorFlowDiagram(): IFlowDiagram {
      // Helper function to map node type to the expected enum type
      const mapNodeType = (type: string): FLOW_DIAGRAM_NODE_TYPE => {
        switch (type) {
          case 'scene':
            return FLOW_DIAGRAM_NODE_TYPE.SCENE;
          case 'condition':
            return FLOW_DIAGRAM_NODE_TYPE.CONDITION;
          case 'condition_multi':
            return FLOW_DIAGRAM_NODE_TYPE.MULTI_CONDITION;
          case 'ab_test':
            return FLOW_DIAGRAM_NODE_TYPE.AB_TEST;
          case 'lineup':
            return FLOW_DIAGRAM_NODE_TYPE.LINEUP;
          default:
            // Fallback to 'lineup' for any unknown types
            console.warn(`Unknown node type: ${type}, defaulting to 'lineup'`);
            return FLOW_DIAGRAM_NODE_TYPE.LINEUP;
        }
      };

      return {
        nodes: self.nodes.map((node) => ({
          id: node.id,
          label: node.label,
          data: node.data,
          lineupId: node.lineupId,
          type: mapNodeType(node.type)
        })) as IFlowDiagram['nodes'],
        edges: self.edges.map((edge) => ({
          source: edge.source,
          target: edge.target,
          sourceHandle: deserializeSourceHandle(edge.sourceHandle),
          index: edge.index === null ? undefined : edge.index,
        })),
      };
    },
  }));
export const ModModel = types.model("Mod", {
  ...ModProps,
  origin: types.optional(types.maybeNull(types.string), null),
});
/*
export const PlayerSettingsModel = types.Date.model("PlayerSettings", {
  jsonData: types.maybeNull(types.frozen())
})
*/

export const VidPartModel = types
  .model("VidPart", {
    ...VidPartProps,
    jsonData: types.maybeNull(types.frozen()),
    assetLayerPairs: types.maybeNull(
      types.map(
        types.array(
          types.model({
            nm: types.maybeNull(types.string),
            uid: types.maybe(types.number),
          })
        )
      )
    ),
    modsArr: types.maybeNull(types.array(types.maybeNull(ModModel))),
    initialModsArr: types.frozen(), // Saved in db
    mods: types.maybeNull(types.string), // Current state
    playerVersionToUse: types.maybeNull(types.string),
    origin: types.maybeNull(types.string),
    // fileName: types.string,
    // tag: types.maybeNull(types.string),
    // createdAt: types.maybeNull(types.string),
  })
  .actions((self) => ({
    /**
     * This method will delete the specific connector from the modsArray based on layerName and type
     * @param layerName
     * @param type
     */
    updateTextMod(
      layerName: string,
      input: TextLayerInput,
      asset: string | undefined,
      additionals: Additional[] | undefined,
      layerUid?: number
    ) {
      const { newValue, newFontValue, experimentId } = input;
      const newMods = self?.modsArr?.map((mod) => {
        const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
        if (
          ((dataStr?.layerUid && layerUid && dataStr.layerUid === layerUid) ||
            dataStr.layerName === layerName) &&
          dataStr.type === "text" &&
          !dataStr?.isDisabled
        ) {
          const newDatastr = JSON.stringify({
            ...dataStr,
            value: newValue || dataStr?.value,
            fontSize: newFontValue || dataStr?.fontValue,
            assetId: asset,
            additionals,
            experimentId: experimentId,
          });
          return {
            ...mod,
            dataStr: newDatastr,
            origin: PLATFORM_TEXT_CONTROL,
            id: mod?.id ?? 0,
            name: mod?.name || "",
            description: mod?.description || "",

          };
        }
        return mod;
      });
      self.modsArr?.replace(newMods || []);
    },
    disableTextMods(mods: string[]) {
      // Find all mods connected to the given text layers and add an isDisabled flag
      const newMods = self?.modsArr?.map((mod) => {
        const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
        if (mods.includes(mod?.id.toString() || "")) {
          return {
            ...mod,
            dataStr: JSON.stringify({ ...dataStr, isDisabled: true }),
            origin: PLATFORM_TEXT_CONTROL,
            id: mod?.id ?? 0,
            name: mod?.name || "",
            description: mod?.description || "",
          };
        }
        return mod;
      });
      // Update the mods array with the new values
      self.modsArr?.replace(newMods || []);
    },
    /**
     * Create a value connector on the layer
     * @param layerName
     * @param value
     * @param asset
     */
    createTextMod(
      layerName: string,
      input: TextLayerInput,
      asset: string | undefined,
      additionals: Additional[] | undefined,
      layerUid?: number
    ) {
      const { newValue, experimentId, newFontValue } = input;
      // Generate a new Id
      let id = 0;
      if (self.modsArr) {
        id =
          self.modsArr.reduce(
            (maxId, mod) => Math.max(mod?.id || 0, maxId),
            -1
          ) + 1 || 0;
      }
      const newMod = {
        dataStr: JSON.stringify({
          layerName,
          layerUid,
          type: "text",
          fontSize: newFontValue,
          value: newValue,
          assetId: asset,
          experimentId,
          additionals,
        }),
        id,
        origin: PLATFORM_TEXT_CONTROL,
        name: "",
        description: "",
      };
      const mods = [...(self.modsArr || []), newMod];
      self.modsArr?.replace(mods);
    },
  }))
  .actions((self) => ({
    // updateJsonData action removed from here
    updateJsonData(jsonData: Animation) {
      const { assetLayerPairs } = processVideoPartJsonData(jsonData);
      self.jsonData = jsonData;
      self.assetLayerPairs = assetLayerPairs;
    },
    setInitialMods(modsArr: any) {
      self.initialModsArr = modsArr;
    },
    /**
     *  - Update the mod to contain the new updates
     */
    updateInteractiveMod(
      interactiveMod: EditVideoInteractiveType,
      input: InteractiveModInput
    ) {
      const newMods = self.modsArr?.map((mod) => {
        if (interactiveMod.id === mod?.id.toString()) {
          const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
          const newDatastr = JSON.stringify({
            ...dataStr,
            ctaName:
              input.newCtaName != null ? input.newCtaName : dataStr.ctaName,
            value: input.newValue != null ? input.newValue : dataStr.value,
            experimentId: input.experimentId,
          });
          return {
            ...mod,
            dataStr: newDatastr,
            origin: PLATFORM_INTERACTIVE_CONTROL,
            name: input.newCtaName != null ? input.newCtaName : mod.name,
          };
        }
        return mod;
      });
      self.modsArr?.replace(newMods || []);
    },
    /**
     *  - Update the mod to contain the new updates
     */
    updateExposedToPlatformMod(
      exposedToPlatformMod: EditVideoExposedToPlatformType,
      input: ExposedToPlatformModInput
    ) {
      const newMods = self.modsArr?.map((mod) => {
        if (exposedToPlatformMod.id === mod?.id.toString()) {
          const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
          const newDatastr = JSON.stringify({
            ...dataStr,
            value:
              input.newValue != null ? input.newValue : dataStr.value || "",
            experimentId: input.experimentId,
          });
          return {
            ...mod,
            dataStr: newDatastr,
            origin: PLATFORM_EXPOSED_CONTROL,
          };
        }
        return mod;
      });
      self.modsArr?.replace(newMods || []);
    },
    /**
     *  - Add Mod to ModsArr (value and origin)
     */
    updateTextMods(textLayer: EditVideoTextType, input: TextLayerInput) {
      const textMod = self.modsArr?.find((mod) => {
        const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
        return (
          (dataStr?.layerUid && textLayer.layerData?.uid
            ? dataStr.layerUid === textLayer.layerData.uid
            : dataStr?.layerName === textLayer.layerData.nm) &&
          dataStr.type === "text" &&
          !dataStr?.isDisabled
        );
      });

      if (textMod) {
        if (input.newValue == null) {
          //Keep value if variant change
          const dataStr = textMod?.dataStr && JSON.parse(textMod.dataStr);
          input.newValue = dataStr?.value;
        }
        return self.updateTextMod(
          textLayer.layerData.nm,
          input,
          textLayer?.asset,
          textLayer.additionals,
          textLayer.layerData?.uid
        );
      }
      return self.createTextMod(
        textLayer.layerData.nm,
        input,
        textLayer?.asset,
        textLayer.additionals,
        textLayer.layerData?.uid
      );
    },
  }));

export const MinisiteConfigModel = types.model("MinisiteConfig", {
  scenes: types.maybeNull(types.array(types.string)),
  title: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  thumbUrl: types.maybeNull(types.string),
  faviconUrl: types.maybeNull(types.string),
  preCreate: types.maybeNull(types.string),
});
export const PlayerSettingsModel = types.model("PlayerSettingsStore", {
  posterFrame: types.maybeNull(types.number),
  showTimeline: types.maybeNull(types.boolean),
  storiesMode: types.maybeNull(types.boolean),
  color_loader: types.maybeNull(types.string),
  color_ctrlBtns: types.maybeNull(types.string),
  color_rail: types.maybeNull(types.string),
  color_progress: types.maybeNull(types.string),
  color_thumb: types.maybeNull(types.string),
  color_bg: types.maybeNull(types.string),
  muted: types.maybeNull(types.boolean),
  autoplay: types.maybeNull(types.boolean),
  autoplay_delay: types.maybeNull(types.number),
  autoReplay: types.maybeNull(types.boolean),
  blingsLogoColor: types.maybeNull(types.string),
  showBlingsLogo: types.maybeNull(types.number),
  loadingImage: types.maybeNull(types.string),
  loadingText: types.maybeNull(types.string),
});

export type IDataFileModel = Instance<typeof DataFileModel>;

const DataPointModel = types.model({
  dataId: types.string,
  sourceId: types.string,
  record: types.frozen(),
});
export type IDataPointModel = Instance<typeof DataPointModel>;
export const OverlayModel = types.model("Overlay", {
  audio: types.optional(
    types.union(
      types.null,
      types.array(
        types.model("OverlayAudio", {
          url: types.string,
          volume: types.optional(types.maybeNull(types.number), 1),
          loop: types.optional(types.maybeNull(types.boolean), true),
        })
      )
    ),
    null
  ),
});
export const ProjectBaseModel = types
  .model("Project", {
    ...ProjectProps,
    account: types.maybeNull(
      types.model({
        id: types.string,
        aliasId: types.string,
        minisiteDomain: types.optional(
          types.union(types.string, types.null, types.undefined),
          ""
        ),
      })
    ),
    workSpaceUpdatedAt: types.optional(types.string, ""),
    updatedAt: types.maybeNull(types.string),
    dataFiles: types.maybeNull(types.array(DataFileModel)),
    workspaceVideoParts: types.optional(types.array(VidPartModel), []),
    publishedVideoParts: types.optional(types.frozen(), []),
    publishedExperiments: types.optional(types.frozen(), []),
    newDPFormData: types.optional(types.frozen(), {}), // immutable !
    saveLiveControlStatus: asyncOpStateType,
    savePerVideoDataStatus: asyncOpStateType,
    saveMiniSiteConfigStatus: asyncOpStateType,
    settings: types.maybeNull(types.frozen()), // immutable !
    settingsDraft: types.maybeNull(types.frozen()),
    settingsDraftInEdit: types.maybeNull(types.frozen()),
    settingsJsonSchemaStrDraft: types.maybeNull(types.string),
    saveModsStatus: asyncOpStateType,
    refreshModsStatus: asyncOpStateType, // when mod is changed in edit video
    createNewDPStatus: asyncOpStateType, // when mod is saved in edit video
    createdDPList: types.optional(types.array(DataPointModel), []),
    isPublishing: types.optional(types.boolean, false),
    publishedAt: types.maybeNull(types.string),
    customHtml: types.maybeNull(types.string),
    // projectVersions: types.maybeNull(undefined), TODO
    playerSettings: types.maybeNull(PlayerSettingsModel), //,  -- check
    minisiteConfig: types.optional(MinisiteConfigModel, {}),
    playerVersionToUse: types.maybeNull(types.string),
    hasFormData: types.boolean,
    dynamicThumbnail: DynamicThumbnailsModel,
    experiments: types.maybeNull(ExperimentStore),
    experimentOptimizationTechnique: types.maybeNull(
      types.frozen(ExperimentOptimizationTechnique)
    ),
    flowDiagram: types.maybeNull(FlowDiagramModel),
    flowDiagramDraft: types.maybeNull(FlowDiagramModel),
    overlay: types.optional(types.maybe(OverlayModel), {}),
    overlayDraft: types.optional(OverlayModel, {}),
    overlayDraftInEdit: types.optional(OverlayModel, {}),
    fontOverrides: types.optional(
      types.maybeNull(types.frozen<Array<FontInput>>()),
      []
    ),
    fontOverridesDraft: types.optional(
      types.maybeNull(types.frozen<Array<FontInput>>()),
      []
    ),
    fontOverridesDraftInEdit: types.optional(
      types.maybeNull(types.frozen<Array<FontInput>>()),
      []
    ),
    instantiatedFromUseCase: types.maybeNull(types.string),
    // Add a timestamp to trigger updates in observers
    lastSubscriptionUpdate: types.optional(types.number, Date.now()),
  })
  .views((self) => ({
    get videoPartNames() {
      const videoPartNames =
        self.publishedVideoParts?.map((vdp: any) => vdp?.name || "") || [];
      return videoPartNames as string[];
    },
    get projectAccountDomain() {
      return self.account?.minisiteDomain || "mp5.live";
    },
    get hasUnpublishedConnnectorChanges() {
      // compare every scene's initialModsArr to the current modsArr
      const savedMods = self.workspaceVideoParts.map((vp) => ({
        name: vp.name,
        modsArr: vp.initialModsArr?.map((mod: any) => ({
          id: mod.id,
          dataStr: mod.dataStr,
          name: mod.name,
          origin: mod.origin,
          description: mod.description,
          // disabled: mod.disabled,
        })),
      }));
      const currentMods = self.publishedVideoParts.map((vp: any) => ({
        name: vp.name,
        modsArr: vp.modsArr?.map((mod: any) => ({
          id: mod.id,
          dataStr: mod.dataStr,
          name: mod.name,
          origin: mod.origin,
          description: mod.description,
          // disabled: mod.disabled,
        })),
      }));
      return (
        !_.isEqual(currentMods, savedMods) ||
        !_.isEqual(
          self.experiments?.unchangedDraftExperimentsCleaned,
          self.experiments?.unchangedExperimentsCleaned
        )
      );
    },
    get isControlDeprecated() {
      const keys = Object.keys(self?.settings || {});
      return keys.length === 0;
    },
    get settingsSchema() {
      if (self.settingsJsonSchemaStr)
        return JSON.parse(self.settingsJsonSchemaStr);
      return JSON.parse(JSON.stringify(emptySchema));
    },
    get settingsSchemaDraft() {
      if (self.settingsJsonSchemaStrDraft)
        return JSON.parse(self.settingsJsonSchemaStrDraft);
      return JSON.parse(JSON.stringify(emptySchema));
    },
    get stateJsonSchema() {
      if (self.stateJsonSchemaStr) return JSON.parse(self.stateJsonSchemaStr);
      return JSON.parse(JSON.stringify(emptySchema));
    },
    // returns true if the draft is up to date with the published version
    get isLiveVersion() {
      const lastPublishedAt = self.publishedAt || self.updatedAt;
      return !!(
        lastPublishedAt &&
        ((self?.workSpaceUpdatedAt &&
          self.workSpaceUpdatedAt < lastPublishedAt) ||
          lastPublishedAt === self?.createdAt)
      );
    },
    get flowDiagramSnapshot(): IFlowDiagram | undefined {
      if (!self.flowDiagramDraft) {
        return undefined;
      }
      const snapshot = getSnapshot(self.flowDiagramDraft);
      if (!snapshot || !snapshot.nodes || !snapshot.edges) {
        return undefined; 
      }
      return {
        nodes: snapshot.nodes as unknown as IFlowDiagram["nodes"],
        edges: snapshot.edges.map((edge: any) => ({
          ...edge,
          sourceHandle: deserializeSourceHandle(edge.sourceHandle),
          index: edge.index === null ? undefined : edge.index,
        })),
      };
    },
  }))
  .actions((self) => ({
    updateSaveModsStatus: updateAsyncStatus(self, "saveModsStatus"),
    updateRefreshModsStatus: updateAsyncStatus(self, "refreshModsStatus"),
    setIsPublishing(isPublishing: boolean) {
      self.isPublishing = isPublishing;
    },
    updateLiveControlData(newSettings: any) {
      self.settings = newSettings;
    },
    updateOverlayDraftInEdit(overlay: OverlayInput) {
      self.overlayDraftInEdit = {
        audio: [
          {
            url: overlay.audio?.[0].url || "",
            volume: overlay.audio?.[0].volume ?? 1,
            loop: overlay.audio?.[0].loop ?? true,
          },
        ],
      };
    },
    updateOverlayDraft(overlay: OverlayInput) {
      self.overlayDraft = {
        audio: [
          {
            url: overlay.audio?.[0].url || "",
            volume: overlay.audio?.[0].volume ?? 1,
            loop: overlay.audio?.[0].loop ?? true,
          },
        ],
      };
    },
    updateOverlay(overlay: OverlayInput) {
      self.overlay =
        overlay && overlay.audio
          ? {
            audio: [
              {
                url: overlay.audio[0].url,
                volume: overlay.audio[0].volume ?? 1,
                loop: overlay.audio[0].loop ?? true,
              },
            ],
          }
          : undefined;
    },

    setCreatedDPList(list: IDataPointModel[]) {
      self.createdDPList.replace(list);
    },
    setPublishedVideoParts(videoParts: any) {
      self.publishedVideoParts = videoParts;
    },
    setPublishedExperiments(experiments: any) {
      self.publishedExperiments = experiments;
    },
    setFontOverrides(fonts: FontInput[]) {
      self.fontOverrides = JSON.parse(JSON.stringify(fonts));
    },
    setFlowDiagramDraft(flowDiagram: any) {
      self.flowDiagramDraft = flowDiagram;
    },
    setFlowDiagram(flowDiagram: any) {
      self.flowDiagram = flowDiagram;
    },
    saveFontOverridesDraft: flow(function* () {
      if (self.fontOverridesDraftInEdit) {
        yield saveFontOverridesDraft(self.id, self.fontOverridesDraftInEdit);
        self.fontOverridesDraft = self.fontOverridesDraftInEdit;
      }
    }),
    setFontOverridesDraftInEdit(fonts: FontInput[]) {
      // Create a new array with new objects to ensure MST detects the change
      const newFonts = fonts.map((font) => ({
        ...font,
        url: font.url || "",
        // Add any other required properties with default values
      }));
      self.fontOverridesDraftInEdit = newFonts;
    },
    replaceWorkspaceVideoParts(updatedParts: IVidPartModel[]) {
      self.workspaceVideoParts.replace(updatedParts);
    },
    triggerSubscriptionUpdate() {
      self.lastSubscriptionUpdate = Date.now();
    },

    updatePlayerSettings(newSettings: any) {
      self.playerSettings = newSettings;
    },
    updateSettingsFromSubscription(settings: string) {
      try {
        const parsedSettings = JSON.parse(settings);
        if (parsedSettings && Object.keys(parsedSettings).length > 0) {
          self.settingsDraft = parsedSettings;
          self.settingsDraftInEdit = JSON.parse(JSON.stringify(parsedSettings));
        }
      } catch (e) {
        console.error("Failed to parse settings from subscription:", e);
      }
    },
    updateOverlayFromSubscription(overlay: any) {
      if (
        !overlay ||
        !overlay.audio ||
        !Array.isArray(overlay.audio) ||
        overlay.audio.length === 0
      )
        return;

      const audioData = overlay.audio || [];
      if (audioData.length > 0) {
        self.overlayDraft = {
          audio: audioData.map((audio: any) => ({
            url: audio.url || "",
            volume: audio.volume ?? 1,
            loop: audio.loop ?? true,
          })),
        };
        self.overlayDraftInEdit = {
          audio: audioData.map((audio: any) => ({
            url: audio.url || "",
            volume: audio.volume ?? 1,
            loop: audio.loop ?? true,
          })),
        };
      }
    },

    updateFontOverridesFromSubscription(fontOverrides: any[]) {
      if (!fontOverrides || !Array.isArray(fontOverrides)) return;

      self.fontOverridesDraft = JSON.parse(JSON.stringify(fontOverrides));
      self.fontOverridesDraftInEdit = JSON.parse(JSON.stringify(fontOverrides));
    },
  }))
  .actions((self) => ({
    updatePlayerSettings(newSettings: any) {
      self.playerSettings = newSettings;
    },

    /**
     * Update the scenes with the latest changes and save in draft. identifiable by type or id
     */
    async saveMod(sceneName: string, layerOrMod: EditVideoTypes) {
      self.updateSaveModsStatus(AsyncOpState.Saving);
      self.experiments?.changedToUnchangedSpecificDraftExperiment(
        layerOrMod?.experimentId
      );
      const scene = self.workspaceVideoParts.find(
        (vp) => vp.name === sceneName
      );
      const initialModsArr = scene?.initialModsArr
        ? [...scene.initialModsArr]
        : [];
      const modsArr = scene?.modsArr || [];
      const indexOfConnectorInitial = initialModsArr.findIndex((mod: any) => {
        if ("id" in layerOrMod) {
          //if mod
          return layerOrMod.id === mod.id.toString();
        } else {
          // Text layer
          const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
          if (dataStr?.layerUid && layerOrMod.layerData?.uid) {
            return (
              dataStr.layerUid === layerOrMod.layerData.uid &&
              dataStr.type === "text" &&
              !dataStr?.isDisabled
            );
          } else {
            return (
              dataStr.layerName === layerOrMod.layerData.nm &&
              dataStr.type === "text" &&
              !dataStr?.isDisabled
            );
          }
        }
      });
      let indexOfConnectorCurrent = -1;
      let indexOfConnectorThatSwitchedToDisabled = -1;
      for (let index = 0; index < modsArr.length; index++) {
        const mod = modsArr[index];
        if ("id" in layerOrMod) {
          if (layerOrMod.id === mod?.id.toString()) {
            indexOfConnectorCurrent = index;
            break;
          }
        } else {
          const dataStr = mod?.dataStr && JSON.parse(mod.dataStr);
          if (dataStr) {
            if (dataStr.layerUid && layerOrMod.layerData?.uid) {
              if (
                dataStr.layerUid === layerOrMod.layerData.uid &&
                dataStr.type === "text"
              ) {
                if (!dataStr.isDisabled) {
                  indexOfConnectorCurrent = index;
                  break;
                } else {
                  const initialDataStr =
                    initialModsArr[indexOfConnectorInitial]?.dataStr &&
                    JSON.parse(
                      initialModsArr[indexOfConnectorInitial]?.dataStr
                    );
                  if (initialDataStr && !initialDataStr.isDisabled) {
                    indexOfConnectorThatSwitchedToDisabled = index;
                  }
                }
              }
            } else if (
              dataStr.layerName === layerOrMod.layerData.nm &&
              dataStr.type === "text"
            ) {
              if (!dataStr.isDisabled) {
                indexOfConnectorCurrent = index;
                break;
              } else {
                const initialDataStr =
                  initialModsArr[indexOfConnectorInitial]?.dataStr &&
                  JSON.parse(initialModsArr[indexOfConnectorInitial]?.dataStr);
                if (initialDataStr && !initialDataStr.isDisabled) {
                  indexOfConnectorThatSwitchedToDisabled = index;
                }
              }
            }
          }
        }
      }
      // if a connector has been disabled and a new one has been created
      // if new connector is disabled and initial is not
      const dataStrInitial =
        initialModsArr[indexOfConnectorInitial]?.dataStr &&
        JSON.parse(initialModsArr[indexOfConnectorInitial].dataStr);
      const conditionMet =
        dataStrInitial &&
        modsArr.some((mod: any) => {
          return (
            mod.id === initialModsArr[indexOfConnectorInitial].id &&
            !dataStrInitial?.isDisabled &&
            !!mod.isDisabled
          );
        });
      if (conditionMet) {
        initialModsArr.push(modsArr?.[indexOfConnectorCurrent]); // add
      }
      // If a connector has been changed
      if (indexOfConnectorInitial > -1 && indexOfConnectorCurrent > -1) {
        // if connector got disabled and added
        if (indexOfConnectorThatSwitchedToDisabled > -1) {
          initialModsArr[indexOfConnectorInitial] =
            modsArr?.[indexOfConnectorThatSwitchedToDisabled]; //disabled old one
          initialModsArr.push(modsArr?.[indexOfConnectorCurrent]); //add new one
        } else {
          initialModsArr[indexOfConnectorInitial] =
            modsArr?.[indexOfConnectorCurrent];
        }
      }
      // If a connector has been deleted
      else if (indexOfConnectorInitial > -1 && indexOfConnectorCurrent === -1) {
        initialModsArr.splice(indexOfConnectorInitial, 1);
      }
      //If a connector has been added
      else if (indexOfConnectorInitial === -1 && indexOfConnectorCurrent > -1) {
        initialModsArr.push(modsArr?.[indexOfConnectorCurrent]);
      }
      try {
        const updatedScenes: VidPartInput[] = self.workspaceVideoParts?.map(
          (vp) => ({
            name: vp.name,
            jsonUrl: vp.jsonUrl,
            modsArr: sceneName === vp.name ? initialModsArr : vp.initialModsArr,
            mods: vp.mods,
            updatedAt: vp.updatedAt,
            playerVersionToUse: vp.playerVersionToUse,
            origin: vp.origin,
            fonts: vp.fonts,
            hasVideo: vp.hasVideo,
            description: vp.description,
          })
        );
        await updateVersion({
          id: self.id,
          videoParts: updatedScenes,
          experiments: self.experiments
            ?.unchangedDraftExperiments as ExperimentInput[],
        });
        scene?.setInitialMods(initialModsArr);
        self.updateSaveModsStatus(AsyncOpState.Success);
      } catch (e) {
        self.updateSaveModsStatus(AsyncOpState.Error);
        console.error("err", e);
      }
    },
    saveOverlay: flow(function* (overlay: OverlayInput) {
      yield Promise.all([
        saveOverlay(self.id, overlay),
        saveOverlayDraft(self.id, overlay),
      ]);
      self.overlay = {
        audio: [
          {
            url: overlay.audio?.[0].url || "",
            volume: overlay.audio?.[0].volume ?? 1,
            loop: overlay.audio?.[0].loop ?? true,
          },
        ],
      };
      self.overlayDraft = {
        audio: [
          {
            url: overlay.audio?.[0].url || "",
            volume: overlay.audio?.[0].volume ?? 1,
            loop: overlay.audio?.[0].loop ?? true,
          },
        ],
      };
    }),
    async saveOverlayDraft() {
      try {
        const overlayDraftInEdit = {
          audio: [
            {
              url: self.overlayDraftInEdit.audio?.[0].url || "",
              volume: self.overlayDraftInEdit.audio?.[0].volume ?? 1,
              loop: self.overlayDraftInEdit.audio?.[0].loop ?? true,
            },
          ],
        };

        await saveOverlayDraft(self.id, overlayDraftInEdit);
        self.updateOverlayDraft(overlayDraftInEdit);
      } catch (e) {
        console.error(e);
      }
    },
    async saveFlowDiagramDraft() {
      try {
        if (self.flowDiagramDraft) {
          const flowDiagram = getSnapshot(self.flowDiagramDraft);
          if(!flowDiagram || !flowDiagram.nodes || !flowDiagram.edges) return;
          const serializedFlowDiagram = {
            nodes: flowDiagram.nodes,
            edges: flowDiagram.edges.map((edge: any) => ({
              ...edge,
              sourceHandle: serializeSourceHandle(edge.sourceHandle),
            })),
          };
          await saveFlowDiagramDraft(self.id, {
            edges: serializedFlowDiagram.edges,
            nodes: serializedFlowDiagram.nodes,
          });
          self.flowDiagram = self.flowDiagramDraft;
        }
      } catch (e) {
        console.error(e);
      }
    },
    async saveFlowDiagram() {
      try {
        if (self.flowDiagram) {
          const flowDiagram = getSnapshot(self.flowDiagram);
          if(!flowDiagram || !flowDiagram.nodes || !flowDiagram.edges) return;
          const serializedFlowDiagram = {
            nodes: flowDiagram.nodes,
            edges: flowDiagram.edges.map((edge: any) => ({
              ...edge,
              sourceHandle: serializeSourceHandle(edge.sourceHandle),
            })),
          };
          await saveFlowDiagram(self.id, {
            edges: serializedFlowDiagram.edges,
            nodes: serializedFlowDiagram.nodes,
          });
          self.flowDiagramDraft = self.flowDiagram;
        }
      }
      catch (e) {
        console.error(e);
      }
    },
/*     
    saveFlowDiagramDraft: flow(function* () {
      if (self.flowDiagramDraft) {
        const flowDiagram = getSnapshot(self.flowDiagramDraft);
        yield saveFlowDiagramDraft(self.id, flowDiagram);
      }
    }), */

    changeSavePlayerSettingsStatus(newStatus: AsyncOpState) {
      self.saveLiveControlStatus = newStatus;
    },
    findModByExperimentId(experimentId: string) {
      let mod = undefined;

      self.publishedVideoParts.forEach((vp: any) => {
        // console.log("vp", vp)
        if (vp.modsArr) {
          vp.modsArr.forEach((m: any) => {
            const modData = JSON.parse(m?.dataStr || "{}");
            // console.log("modData", modData.experimentId, experimentId)
            if (modData.experimentId === experimentId) {
              mod = m;
            }
          });
        }
      });
      return mod;
    },
    async savePlayerSettings(
      playerSettings: PlayerSettingsInput,
      thumbnail?: string
    ) {
      const beforePlayerSettings = self.playerSettings;
      const stringifiedPlayerSettings = JSON.stringify(self.playerSettings);
      this.updatePlayerSettings({ ...beforePlayerSettings, playerSettings });
      this.changeSavePlayerSettingsStatus(AsyncOpState.Saving);
      try {
        const cpi = { id: self.id, playerSettings, thumbS3Url: thumbnail };
        await savePublished(cpi);
      } catch (e) {
        console.error("err", e);
        this.changeSavePlayerSettingsStatus(AsyncOpState.Error);
        setTimeout(() => {
          this.changeSavePlayerSettingsStatus(INITIAL_ASYNC_OP_STATE);
          this.updatePlayerSettings(JSON.parse(stringifiedPlayerSettings));
        }, 3000);
      }
    },
    async uploadAndSetThumbnail(thumbnail: string) {
      try {
        const cpi = { id: self.id, thumbS3Url: thumbnail };
        await savePublished(cpi);
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Get the published version, then update it with the new videopart changes and upload it.
     * Finally, load projects
     */
    async publishProject() {
      self.setIsPublishing(true);
      const latestProjectVersion = await getLatestProjectVersion(self.id);
      if (!latestProjectVersion) {
        return;
      }
      const toPublishInput: UpdateProjectInput = latestProjectVersion;

      const updatedVideoparts =
        latestProjectVersion.videoParts?.map((vp) => {
          const { __typename, ...rest } = vp;
          const workSpaceVideopart = self.workspaceVideoParts.find(
            (wvp) => wvp.name === vp.name
          );
          const modsArray: ModInput[] | undefined =
            workSpaceVideopart?.modsArr?.map((mod) => ({
              dataStr: mod?.dataStr || "",
              id: mod?.id || 0,
              origin: mod?.origin || null,
              name: mod?.name,
              description: mod?.description || null,
            }));
          return { ...rest, modsArr: modsArray || vp.modsArr }; //Update the mods Array
        }) || [];
      toPublishInput.videoParts = updatedVideoparts;
      const variantStore = self.experiments;
      const updatedExperiments =
        variantStore?.unchangedDraftExperiments as ExperimentInput[];
      if (updatedExperiments.length > 0) {
        toPublishInput.experiments = updatedExperiments;
      }

      toPublishInput["experimentOptimizationTechnique"] =
        variantStore?.selectedExperimentOptimizationTechnique ||
        ExperimentOptimizationTechnique.DISABLED;

      const { overlay } = latestProjectVersion;
      const { audio } = overlay || {};
      toPublishInput.overlay = { audio: audio || [] };
      if (toPublishInput) await publishProject(toPublishInput);
      self.setIsPublishing(false);
      self.setPublishedVideoParts(updatedVideoparts);
      self.setPublishedExperiments(
        toPublishInput?.experiments?.map((exp) => ({
          id: exp.id,
          options: exp.variants || [],
          type: exp.type,
        }))
      );

      self.updateLiveControlData(JSON.parse(toPublishInput.settings || "{}"));
      const audioWithoutUndefined = (toPublishInput.overlay?.audio || []).map(
        (audio) => [
          {
            url: audio.url || "",
            volume: audio.volume ?? null,
            loop: audio.loop ?? null,
          },
        ]
      );

      self.updateOverlay({
        audio: audioWithoutUndefined[0] as OverlayAudioInput[],
      });
      self.setFontOverrides(toPublishInput.fontOverrides || []);

      if (self.id) await rootStore.projectsStore.loadProject(self.id);
    },
    async changeExperimentOptimizationType() {
      const currentOptimizationTechnique =
        self.experiments?.selectedExperimentOptimizationTechnique ||
        ExperimentOptimizationTechnique.DISABLED;
      const currentProjectId = self.id;
      changeProjectExperimentOptimizationTechnique(
        currentProjectId,
        currentOptimizationTechnique
      );
    },
    setDynamicThumbnailId(id: string) {
      // Push the new thumbnail as a snapshot
      self.dynamicThumbnail.id = id;
    },

    downloadExampleCSV() {
      const perVideoExample = self.stateJsonSchema.examples[0] || null;
      if (!perVideoExample) throw new Error("Example data not set");
      const flattenData = flattenObject(perVideoExample);
      const keys = ["id", ...Object.keys(flattenData)];
      const values = ["example_id", ...Object.values(flattenData)] as (
        | string
        | number
      )[];
      const csv = generateCSV(keys, [values]);
      downloadFile(csv, `${self.title}.csv`, "text/csv");
    },

    /**
     * Add a new data point to the project. Will check if the data point already exists
     * @param dataPoint A data point that should be added to the project
     */
    addNewDataPointToProject(dataPoint: IDataPointModel) {
      // Check if the data point already exists
      const existingDataPoint = self.createdDPList.find(
        (dp) => dp.dataId === dataPoint.dataId
      );
      if (existingDataPoint) {
        // If the data point already exists, update it
        this.updateDataPoint(dataPoint);
        return;
      }
      // Add the data point to the project and order it alphabetically
      self.createdDPList.push(dataPoint);
      self.createdDPList = self.createdDPList.sort((a, b) =>
        a.dataId.localeCompare(b.dataId)
      );
    },

    /**
     * Update a data point in the project
     * @param dataPoint A data point that should be updated
     */
    updateDataPoint(dataPoint: IDataPointModel) {
      const index = self.createdDPList.findIndex(
        (dp) => dp.dataId === dataPoint.dataId
      );
      if (index > -1) {
        self.createdDPList[index] = dataPoint;
      }
    },
  }));

export const ProjectModel = ProjectBaseModel.actions((self) => {
  let videoPartsSubscription: Subscription | null = null;

  function handleSubscriptionMessage(data: ProjectVersion | null) {
    if (!data) return;
    if (data.settings) {
      self.updateSettingsFromSubscription(data.settings);
    }

    if (data.videoParts && Array.isArray(data.videoParts)) {
      // Create a deep copy of the current workspaceVideoParts
      const updatedVideoParts = self.workspaceVideoParts.map((localVp) => {
        // Find the matching video part from the incoming data
        const incomingVp = data.videoParts?.find(
          (vp) => vp.name === localVp.name
        );

        if (!incomingVp) {
          // If no matching video part is found, keep the original unchanged
          return localVp;
        }

        // Create an updated copy with the changes
        const updatedVp = { ...localVp };

        // Update modsArr if present
        if (incomingVp.modsArr) {
          (updatedVp.initialModsArr as IModModel[]) = incomingVp.modsArr.map(
            (mod) => ({
              dataStr: mod.dataStr,
              id: mod.id,
              origin: mod.origin ?? null,
              name: mod.name ?? null,
              description: mod.description ?? null,
            })
          );
          (updatedVp.modsArr as IModModel[]) = incomingVp.modsArr.map(
            (mod) => ({
              dataStr: mod.dataStr,
              id: mod.id,
              origin: mod.origin ?? null,
              name: mod.name ?? null,
              description: mod.description ?? null,
            })
          );
        }

        // Update other properties
        if (incomingVp.jsonUrl) {
          updatedVp.jsonUrl = incomingVp.jsonUrl;
          // Note: JSON data fetching and processing moved outside this map
        }
        if (incomingVp.updatedAt) updatedVp.updatedAt = incomingVp.updatedAt;
        if (incomingVp.hasVideo !== undefined && incomingVp.hasVideo !== null) {
          updatedVp.hasVideo = incomingVp.hasVideo;
        }
        if (incomingVp.description) {
          updatedVp.description = incomingVp.description;
        }
        if (incomingVp.fonts) {
          // Check if incomingVp.fonts is an array before mapping
          updatedVp.fonts = incomingVp.fonts as any;
        }
        if (incomingVp.jsonUrl) {
          updatedVp.jsonUrl = incomingVp.jsonUrl;
          getJsonDataFromUrl(incomingVp.jsonUrl)
            .then((jsonData) => {
              const { assetLayerPairs } = processVideoPartJsonData(jsonData);
              updatedVp.jsonData = jsonData;
              updatedVp.assetLayerPairs = assetLayerPairs;
            })
            .catch((error) => {
              console.error(
                `Error fetching JSON data from URL ${incomingVp.jsonUrl}:`,
                error
              );
            });
        }

        return updatedVp;
      });

      self.replaceWorkspaceVideoParts(updatedVideoParts);
    }

    if (data.overlay) {
      self.updateOverlayFromSubscription(data.overlay);
    }

    if (data.fontOverrides) {
      self.updateFontOverridesFromSubscription(data.fontOverrides);
    }

    self.triggerSubscriptionUpdate();
  }

  async function subscribeToWorkspaceVideoParts(numRetries = 0) {
    if (videoPartsSubscription) {
      videoPartsSubscription.unsubscribe();
      videoPartsSubscription = null;
    }
    
    if (numRetries > 5) {
      console.error("Max retries reached for subscribing to video parts.");
      return;
    }

    const accountId = self.account?.id;
    if (!accountId) {
      console.error("Cannot subscribe: account ID is missing");
      return;
    }

    console.log("Subscribing to project version updates:", self.id);

    videoPartsSubscription = subscriptionManager.subscribeToProjectVersion(
      accountId,
      self.id,
      (data: ProjectVersion) => {
        console.log("Received PubSub message for WorkspaceVideoParts:", data);
        handleSubscriptionMessage(data);
      },
      (err: any) => {
        console.error("WorkspaceVideoParts PubSub subscription error", err);
        // Retry logic is handled by subscriptionManager
      }
    );

    const latestDraft = await getLatestProjectVersion(self.id);
    if (latestDraft) {
      handleSubscriptionMessage(latestDraft);
    }
  }

  return {
    afterCreate() {
      subscribeToWorkspaceVideoParts();
    },
    beforeDestroy() {
      if (videoPartsSubscription) {
        videoPartsSubscription.unsubscribe();
        videoPartsSubscription = null;
      }
    },
  };
})
  .actions(createDataPointActions)
  .actions(saveSchemasActions)
  .actions(saveFileNameActions)
  .actions(saveMiniSiteConfigActions);

export type IProjectBaseModel = Instance<typeof ProjectBaseModel>;
export type IProjectModel = Instance<typeof ProjectModel>;
export type IVidPartModel = Instance<typeof VidPartModel>;
export type IModModel = Instance<typeof ModModel>;
export type IFlowDiagramModel = Instance<typeof FlowDiagramModel>;
